import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {Heart, Menu, X, LogIn, LogOut, LayoutDashboard, Sun, Moon, ChevronDown} from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'
import AdminLoginModal from '@/components/AdminLoginModal'
import type { SiteSetting } from '@/types'

const NAV_ITEMS = [
  { label: 'Início', href: '/' },
  { label: 'Sobre Nós', href: '/#sobre' },
  { label: 'Estrutura', href: '/#estrutura' },
  { label: 'Instagram', href: '/#instagram' },
  { label: 'Como Ajudar', href: '/#doacao' },
  { label: 'Depoimentos', href: '/#depoimentos' },
  { label: 'Contato', href: '/#contato' },
]

export default function Header() {
  const { theme, toggleTheme } = useTheme()
  const { isAuthenticated } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [siteTitle, setSiteTitle] = useState('(PALACIO DA SABEDORIA')
  const [logoImage, setLogoImage] = useState('')
  const [scrolled, setScrolled] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [titleRes, logoRes] = await Promise.all([
          lumi.entities.site_settings.list({ filter: { key: 'site_title' }, limit: 1 }),
          lumi.entities.site_settings.list({ filter: { key: 'logo_image' }, limit: 1 }),
        ])
        if (titleRes.list[0]) setSiteTitle((titleRes.list[0] as SiteSetting).value)
        if (logoRes.list[0]) setLogoImage((logoRes.list[0] as SiteSetting).value)
      } catch (e) {
        console.error('Erro ao carregar header:', e)
      }
    }
    fetchSettings()
  }, [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleLogout = async () => {
    try {
      await lumi.auth.signOut()
      navigate('/')
    } catch (e) {
      console.error('Erro ao sair:', e)
    }
  }

  const scrollTo = (id: string) => {
    setMobileMenuOpen(false)
    if (id.startsWith('/#')) {
      const el = document.getElementById(id.replace('/#', ''))
      if (el) { el.scrollIntoView({ behavior: 'smooth' }); return }
    }
    navigate(id)
  }

  return (
    <>
      <header
        className="w-full sticky top-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(255,255,255,0.97)' : '#ffffff',
          boxShadow: scrolled ? '0 2px 20px rgba(26,86,219,0.13)' : '0 1px 0 rgba(26,86,219,0.08)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 flex-shrink-0 group">
              {logoImage ? (
                <img src={logoImage} alt={siteTitle} className="h-14 w-auto object-contain" />
              ) : (
                <div className="flex items-center gap-2">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm"
                    style={{ background: '#1A56DB' }}
                  >
                    <span className="text-white font-black text-xs tracking-tight leading-tight text-center px-0.5">PS</span>
                  </div>
                  <div className="leading-tight hidden sm:block">
                    <div className="font-black text-sm text-gray-900" style={{ letterSpacing: '-0.01em' }}>PALÁCIO</div>
                    <div className="text-xs text-gray-500 font-medium">DOS IDOSOS</div>
                  </div>
                </div>
              )}
            </Link>

            {/* Nav desktop */}
            <nav className="hidden lg:flex items-center gap-0.5" aria-label="Navegação principal">
              {NAV_ITEMS.map(item => (
                <button
                  key={item.label}
                  onClick={() => scrollTo(item.href)}
                  className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-lg transition-all relative group"
                >
                  {item.label}
                  <span
                    className="absolute bottom-1 left-3 right-3 h-0.5 rounded-full scale-x-0 group-hover:scale-x-100 transition-transform origin-center"
                    style={{ background: '#1A56DB' }}
                  />
                </button>
              ))}
            </nav>

            {/* Acções direita */}
            <div className="flex items-center gap-2">
              {/* Tema */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                aria-label={theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
              >
                {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
              </button>

              {/* Admin */}
              {isAuthenticated ? (
                <div className="hidden sm:flex items-center gap-1">
                  <Link
                    to="/admin"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-all"
                  >
                    <LayoutDashboard size={14} />
                    Painel
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                    title="Sair"
                  >
                    <LogOut size={16} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setLoginModalOpen(true)}
                  className="hidden sm:flex p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                  title="Entrar"
                >
                  <LogIn size={17} />
                </button>
              )}

              {/* Botão Fazer Doação */}
              <button
                onClick={() => scrollTo('/#doacao')}
                className="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white transition-all shadow-sm"
                style={{ background: '#1A56DB' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#1447C0')}
                onMouseLeave={e => (e.currentTarget.style.background = '#1A56DB')}
              >
                <Heart size={14} />
                FAZER DOAÇÃO
              </button>

              {/* Menu mobile */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-all"
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Menu mobile */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-100 bg-white">
            <div className="px-4 py-4 space-y-1">
              {NAV_ITEMS.map(item => (
                <button
                  key={item.label}
                  onClick={() => scrollTo(item.href)}
                  className="w-full text-left px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-all"
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-2 border-t border-gray-100 flex gap-2">
                <button
                  onClick={() => { scrollTo('/#doacao'); setMobileMenuOpen(false) }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-bold text-white"
                  style={{ background: '#1A56DB' }}
                >
                  <Heart size={14} />
                  Fazer Doação
                </button>
                {!isAuthenticated && (
                  <button
                    onClick={() => { setLoginModalOpen(true); setMobileMenuOpen(false) }}
                    className="px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-200"
                  >
                    <LogIn size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <AdminLoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </>
  )
}
